/**
 * Sign Out Page
 *
 * Handles user sign out by clearing the Supabase session
 * and redirecting to the login page.
 *
 * @fileoverview Sign out handling page
 *
 * @example
 * // In router
 * <Route path="/signout" element={<SignOut />} />
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  VStack,
  Heading,
  Text,
  Spinner,
  Center,
  Alert,
  AlertIcon,
  Button,
  Icon,
} from '@chakra-ui/react';
import { MdLogout } from 'react-icons/md';
import { useAuth } from '../hooks/useAuth';
import { toUserMessage } from '../lib/apiError';

/**
 * Sign Out Page Component
 *
 * Automatically signs out the user and redirects to the home page.
 * Shows loading state during sign out and error state if sign out fails.
 *
 * Flow:
 * 1. Component mounts
 * 2. Calls signOut from auth context
 * 3. On success, redirects to home page
 * 4. On error, displays error message with retry option
 *
 * @returns Sign out page component
 */
export const SignOut: React.FC = () => {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [isSigningOut, setIsSigningOut] = useState(true);

  /**
   * Perform sign out and redirect
   */
  const performSignOut = async (): Promise<void> => {
    setError(null);
    setIsSigningOut(true);

    try {
      await signOut();

      // Clear any local storage items
      localStorage.removeItem('link_token');
      localStorage.removeItem('oauth_state_id');
      localStorage.removeItem('xacto_support_mode');
      localStorage.removeItem('xacto_signup_tenant_id');
      sessionStorage.removeItem('xacto_plaid_link_token');

      // Redirect to home page after short delay
      setTimeout(() => {
        navigate('/', { replace: true });
      }, 500);
    } catch (err) {
      setError(toUserMessage(err, "We couldn't sign out. Please try again."));
      setIsSigningOut(false);
    }
  };

  // Perform sign out on mount
  useEffect(() => {
    performSignOut();
  }, []);

  // Show error state
  if (error) {
    return (
      <Center h="100vh" bg="bg.primary">
        <VStack spacing={6} maxW="md" p={8}>
          <Alert
            status="error"
            variant="subtle"
            bg="red.900"
            borderRadius="md"
          >
            <AlertIcon color="red.400" />
            <VStack align="start" spacing={1}>
              <Text color="red.200" fontWeight="medium">
                Sign Out Failed
              </Text>
              <Text color="red.300" fontSize="sm">
                {error}
              </Text>
            </VStack>
          </Alert>

          <Button
            leftIcon={<Icon as={MdLogout} />}
            colorScheme="red"
            variant="outline"
            onClick={performSignOut}
          >
            Try Again
          </Button>

          <Button
            variant="ghost"
            color="text.secondary"
            onClick={() => navigate('/home')}
          >
            Go Back
          </Button>
        </VStack>
      </Center>
    );
  }

  // Show loading state
  return (
    <Center h="100vh" bg="bg.primary">
      <VStack spacing={6}>
        <Spinner
          size="xl"
          color="accent.primary"
          thickness="4px"
        />
        <VStack spacing={2}>
          <Heading size="md" color="text.primary">
            Signing Out
          </Heading>
          <Text color="text.secondary">
            Please wait while we sign you out...
          </Text>
        </VStack>
      </VStack>
    </Center>
  );
};

export default SignOut;
